import { FocusEvent, KeyboardEvent, useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';

// import { Typography } from '@mui/material';
import { useTopicStore, useTopicViewerStore } from '@/dmdrive/topic/components/Topic/TopicStore';
// import { invalidateLimitsRange } from '@/dmdrive/topic/helpers/invalidateLimitsRange';
import { InputField } from '@/dmdrive/ui-kit/components';

import { LimitControlsField, LimitControlsRoot, PaddedTypography } from './LimitControls.styles';

function LimitControls() {
  const { isControlDisabled } = useTopicStore();
  const { viewerSettings, isHistogramCalcInProgress } = useTopicViewerStore();

  const { scopeUpperValue, scopeLowerValue, lowerValuePD, upperValuePD, apply } = viewerSettings;

  const lowerRef = useRef<Nullable<HTMLInputElement>>(null);
  const upperRef = useRef<Nullable<HTMLInputElement>>(null);

  const pdLowerRef = useRef<Nullable<HTMLInputElement>>(null);
  const pdUpperRef = useRef<Nullable<HTMLInputElement>>(null);

  const setLowerInputValue = useCallback((value: number) => {
    if (lowerRef.current) {
      lowerRef.current.value = value.toFixed(3);
    }
  }, []);

  const setUpperInputValue = useCallback((value: number) => {
    if (upperRef.current) {
      upperRef.current.value = value.toFixed(3);
    }
  }, []);

  const setPdLowerInputValue = useCallback((value: number) => {
    if (pdLowerRef.current) {
      pdLowerRef.current.value = value.toFixed(3);
    }
  }, []);

  const setPdUpperInputValue = useCallback((value: number) => {
    if (pdUpperRef.current) {
      pdUpperRef.current.value = value.toFixed(3);
    }
  }, []);

  // const setHistogramRanges = useCallback(
  //   (range: [number, number]) => {

  //     const [lower, upper] = invalidateLimitsRange(range, isPaletteZeroPoint);

  //     console.log(lower, upper);

  //     apply({
  //       histogramLowerValue: lower,
  //       histogramUpperValue: upper,
  //     });
  //   },
  //   [isPaletteZeroPoint],
  // );

  const setScopeAndPd = useCallback((range: [number, number, number, number]) => {
    apply({
      scopeLowerValue: range[0],
      scopeUpperValue: range[1],
      lowerValuePD: range[2],
      upperValuePD: range[3],
    });
  }, []);

  const onBlurHandler = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const { name, value } = e.target;

      if (!value) {
        setScopeAndPd([scopeLowerValue, scopeUpperValue, lowerValuePD, upperValuePD]);

        return;
      }

      const numeric = +value.replace(/\d+(?:-\d+)+/g, '');

      // console.log(name, value, numeric);

      let scopeLower = scopeLowerValue;
      let scopeUpper = scopeUpperValue;

      let pdLower = lowerValuePD;
      let pdUpper = upperValuePD;

      if (name === 'scope.lower') {
        scopeLower = numeric;
        setLowerInputValue(scopeLower);
      }

      if (name === 'scope.upper') {
        scopeUpper = numeric;

        setUpperInputValue(scopeUpper);
      }

      if (name === 'pd.lower') {
        pdLower = numeric;
        setPdLowerInputValue(pdLower);
      }

      if (name === 'pd.upper') {
        pdUpper = numeric;
        setPdUpperInputValue(pdUpper);
      }

      setScopeAndPd([scopeLower, scopeUpper, pdLower, pdUpper]);
    },
    [
      scopeLowerValue,
      scopeUpperValue,
      lowerValuePD,
      upperValuePD,
      setLowerInputValue,
      setUpperInputValue,
      setPdLowerInputValue,
      setPdUpperInputValue,
    ],
  );

  const onKeyUpHandler = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.code !== 'Enter') {
        return;
      }

      onBlurHandler(e as unknown as FocusEvent<HTMLInputElement>);
    },
    [onBlurHandler],
  );

  useEffect(() => {
    // console.log("useEffect",scopeUpperValue,scopeLowerValue,lowerValuePD,upperValuePD );

    setLowerInputValue(scopeLowerValue);
    setUpperInputValue(scopeUpperValue);

    setPdLowerInputValue(lowerValuePD);
    setPdUpperInputValue(upperValuePD);
  }, [scopeUpperValue, scopeLowerValue, lowerValuePD, upperValuePD]);

  return (
    <LimitControlsRoot>
      <div>
        <PaddedTypography align="center">Scope</PaddedTypography>

        <LimitControlsField>
          <InputField
            ref={lowerRef}
            size="small"
            variant="standard"
            name="scope.lower"
            onBlur={onBlurHandler}
            defaultValue={scopeLowerValue}
            disabled={isHistogramCalcInProgress || isControlDisabled}
            onKeyUp={onKeyUpHandler}
          />

          <InputField
            ref={upperRef}
            size="small"
            variant="standard"
            name="scope.upper"
            onBlur={onBlurHandler}
            defaultValue={scopeUpperValue}
            disabled={isHistogramCalcInProgress || isControlDisabled}
            onKeyUp={onKeyUpHandler}
          />
        </LimitControlsField>
      </div>

      <div>
        <PaddedTypography align="center">Permissible deviation</PaddedTypography>

        <LimitControlsField>
          <InputField
            ref={pdLowerRef}
            size="small"
            variant="standard"
            name="pd.lower"
            onBlur={onBlurHandler}
            defaultValue={lowerValuePD}
            disabled={isHistogramCalcInProgress || isControlDisabled}
            onKeyUp={onKeyUpHandler}
          />

          <InputField
            ref={pdUpperRef}
            size="small"
            variant="standard"
            name="pd.upper"
            onBlur={onBlurHandler}
            defaultValue={upperValuePD}
            disabled={isHistogramCalcInProgress || isControlDisabled}
            onKeyUp={onKeyUpHandler}
          />
        </LimitControlsField>
      </div>
    </LimitControlsRoot>
  );
}

export default observer(LimitControls);
